import cookies from 'browser-cookies';
import { useClientAuth } from '@stitch-fix/sf-next';
import { RET_COOKIE_NAME } from '@stitch-fix/sf-next/middleware';

/**
 * This function takes a new URL string as an argument and appends all query parameters from the
 * current page's URL that start with 'utm_'.
 *
 * Accepts either a full URL (e.g. 'https://stitchfix.com/login') or a URL path (e.g. '/login'),
 * and returns the same form as given.
 *
 * @param {string} newUrl - The URL to which 'utm_' parameters will be appended.
 * @returns {string} - The new URL string with 'utm_' parameters appended.
 *
 * @example
 * // Given the current URL is 'https://www.stitchfix.com?utm_source=google'
 * addUtmParamsToUrl('/login'); // => returns '/login?utm_source=google'
 * addUtmParamsToUrl('https://stitchfix.com/login'); // => returns 'https://stitchfix.com/login?utm_source=google'
 */
const addUtmParamsToUrl = (newUrl: string): string => {
  const url = new URL(window.location.href);
  // `newUrl` may be just the path name, but `URL` requires a full URL. So we
  // add the current base URL to fill in the host if necessary.
  const newUrlObj = new URL(newUrl, window.location.href);

  url.searchParams.forEach((value, key) => {
    if (key.startsWith('utm_')) {
      newUrlObj.searchParams.set(key, value);
    }
  });

  // If given a URL path (e.g. '/login'), return a URL path back
  if (newUrl.startsWith('/')) {
    return newUrlObj.pathname + newUrlObj.search + newUrlObj.hash;
  }

  // Otherwise, return the full URL
  return newUrlObj.toString();
};

/**
 * This hook returns a callback that can be used to gate a function behind
 * authentication. If the user is not logged in, the callback will redirect
 * the user to the login page.
 * @example
 * const gatedCallback = useAuthGatedCallback(() => {
 *    // do something
 *  });
 *  // ...
 *  // If the user is not logged in, clicking the button will redirect to the login page.
 *  // If the user is logged in, clicking the button will call the callback.
 *  <Button onClick={gatedCallback}>Click me</Button>
 *  // ...
 *
 * @param callback - The callback to be gated behind authentication
 */
export const useAuthGatedCallback = (
  callback: (...args: unknown[]) => unknown,
) => {
  const { authStatus } = useClientAuth();
  const loggedIn = authStatus === 'authenticated';

  if (loggedIn) return callback;

  return () => {
    // Set the return URL cookie so that after logging in, the
    // client will be redirected back to the page they were on.
    // The cookie is typically set by Rails, but there's no restriction
    // on the client setting it as well. See: https://stitchfix.atlassian.net/browse/KR-62
    cookies.set(RET_COOKIE_NAME, window.location.href);

    // Using `replace` vs `assign` so that clicking the Back button does *not*
    // go back to the URL (which would case another redirect to the URL).
    // Using `replace` simulates a server-side redirect where we redirect to
    // the URL without ever actually seeing the route.
    window.location.replace(addUtmParamsToUrl('/login'));
  };
};
